import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { loc } from "../lib/localize";
import { Coins, Inbox } from "lucide-react";

export default function BetsPage() {
  const { t, lang } = useLanguage();
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bets/mine").then(({ data }) => setBets(data)).finally(() => setLoading(false));
  }, []);

  const totalStaked = bets.reduce((s, b) => s + b.stake, 0);
  const totalPotential = bets.reduce((s, b) => s + b.potential_payout, 0);

  return (
    <main className="relative z-10 mx-auto max-w-5xl px-6 py-16 md:px-10">
      <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("bets.eyebrow")}</span>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("bets.title")}</h1>

      <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:max-w-md">
        <div className="bg-card p-6">
          <p className="font-heading text-3xl font-black">{totalStaked}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("bets.totalStaked")}</p>
        </div>
        <div className="bg-card p-6">
          <p className="font-heading text-3xl font-black text-credit">{totalPotential.toFixed(0)}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("bets.possiblePayout")}</p>
        </div>
      </div>

      {loading ? (
        <p className="mt-10 text-muted-foreground">{t("common.loading")}</p>
      ) : bets.length === 0 ? (
        <div className="mt-10 flex flex-col items-center gap-4 border border-dashed border-white/15 py-24 text-center">
          <Inbox className="h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground">{t("bets.empty")}</p>
          <Link to="/wiki" className="text-primary hover:underline">{t("bets.explore")}</Link>
        </div>
      ) : (
        <div className="mt-10 overflow-hidden border border-white/10">
          <table className="w-full text-sm" data-testid="bets-table">
            <thead className="border-b border-white/10 bg-card">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="p-4">{t("bets.colArticle")}</th>
                <th className="p-4">{t("bets.colSide")}</th>
                <th className="p-4 text-right">{t("bets.colStake")}</th>
                <th className="p-4 text-right">{t("bets.colFactor")}</th>
                <th className="p-4 text-right">{t("bets.colPayout")}</th>
              </tr>
            </thead>
            <tbody>
              {bets.map((b) => (
                <tr key={b.id} className="border-b border-white/5 transition-colors hover:bg-card">
                  <td className="p-4">
                    <Link to={`/wiki/${b.article_slug}`} className="font-medium hover:text-primary">{loc(b, "article_title", lang)}</Link>
                  </td>
                  <td className="p-4">
                    <span className={b.side === "agree" ? "text-truth" : "text-lie"}>
                      {b.side === "agree" ? t("article.agree") : t("article.disagree")}
                    </span>
                  </td>
                  <td className="p-4 text-right font-mono">
                    <span className="inline-flex items-center gap-1"><Coins className="h-3 w-3 text-credit" />{b.stake}</span>
                  </td>
                  <td className="p-4 text-right font-mono text-credit">{b.multiplier}×</td>
                  <td className="p-4 text-right font-heading font-bold text-credit">{b.potential_payout.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
